import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import Input from '../../../form/Input';
import Select from '../../../form/Select';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

interface IFilter {
  companyNameRef: React.RefObject<HTMLInputElement>;
  companyNumberRef: React.RefObject<HTMLInputElement>;
  ceoRef: React.RefObject<HTMLInputElement>;
  telRef: React.RefObject<HTMLInputElement>;
  idRef: React.RefObject<HTMLInputElement>;
  bizRef: React.RefObject<HTMLInputElement>;
}

const FranchiseFilter = ({
    companyNameRef,
    companyNumberRef,
    ceoRef,
    telRef,
    idRef,
    bizRef
}: IFilter) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pageSize, setPageSize] = useState<string>('20');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);

    if (currentQuery.size) {
      setPageSize(currentQuery.size as string);
    } else {
      setPageSize('20'); // 기본값
    }

    if (currentQuery.companyName && companyNameRef.current) {
      companyNameRef.current.value = currentQuery.companyName as string;
    } else if (companyNameRef.current) {
      companyNameRef.current.value = ''; // 초기화
    }

    if (currentQuery.companyNumber && companyNumberRef.current) {
      companyNumberRef.current.value = currentQuery.companyNumber as string;
    } else if (companyNumberRef.current) {
      companyNumberRef.current.value = ''; // 초기화
    }

    if (currentQuery.ceo && ceoRef.current) {
      ceoRef.current.value = currentQuery.ceo as string;
    } else if (ceoRef.current) {
      ceoRef.current.value = ''; // 초기화
    }

    if (currentQuery.telNo && telRef.current) {
      telRef.current.value = currentQuery.telNo as string;
    } else if (telRef.current) {
      telRef.current.value = ''; // 초기화
    }

    if (currentQuery.id && idRef.current) {
      idRef.current.value = currentQuery.id as string;
    } else if (idRef.current) {
      idRef.current.value = ''; // 초기화
    }

    if (currentQuery.bizProfileName && bizRef.current) {
      bizRef.current.value = currentQuery.bizProfileName as string;
    } else if (bizRef.current) {
      bizRef.current.value = ''; // 초기화
    }
  }, [ceoRef, companyNameRef, location.search, telRef, idRef, bizRef, companyNumberRef]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>, replaceAll: boolean = false) => {
      let updatedQuery: Record<string, string | undefined>;

      if (replaceAll) {
        // 전체 교체 모드: 기존 쿼리 무시하고 새 값만 사용
        updatedQuery = { ...updatedFields };
      } else {
        // 병합 모드: 기존 쿼리에 새 값 병합
        const currentQuery = queryString.parse(location.search);
        const normalizedQuery: Record<string, string | undefined> = {};

        // queryString.parse 결과를 string | undefined로 정규화
        Object.keys(currentQuery).forEach(key => {
          const value = currentQuery[key];
          if (typeof value === 'string') {
            normalizedQuery[key] = value;
          }
        });

        updatedQuery = {
          ...normalizedQuery,
          ...updatedFields,
        };
      }

      // undefined 또는 빈 문자열 값 제거
      Object.keys(updatedQuery).forEach(key => {
        if (updatedQuery[key] === undefined || updatedQuery[key] === '') {
          delete updatedQuery[key];
        }
      });

      const newQueryString = queryString.stringify(updatedQuery);
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const onChangePageSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setPageSize(value);
      updateQueryString({ size: value, page: '0' });
    },
    [updateQueryString]
  );

  // 사업자번호 포맷팅 함수
  const formatBusinessNumber = useCallback((value: string) => {
    let businessInput = value.replace(/\D/g, '');
    
    if (businessInput.length > 3 && businessInput.length <= 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3)}`;
    } else if (businessInput.length > 5) {
      businessInput = `${businessInput.slice(0, 3)}-${businessInput.slice(3, 5)}-${businessInput.slice(5, 10)}`;
    }
    
    return businessInput;
  }, []);
  
  // 전화번호 포맷팅 함수
  const formatPhoneNumber = useCallback((value: string) => {
    let phoneInput = value.replace(/\D/g, '');
    
    if (phoneInput.length > 3 && phoneInput.length <= 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3)}`;
    } else if (phoneInput.length > 7) {
      phoneInput = `${phoneInput.slice(0, 3)}-${phoneInput.slice(3, 7)}-${phoneInput.slice(7, 11)}`;
    }
    
    return phoneInput;
  }, []);
  
  // 사업자번호 입력 핸들러
  const handleBusinessNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBusinessNumber(e.target.value);
    if (companyNumberRef.current) {
      companyNumberRef.current.value = formatted;
    }
  }, [formatBusinessNumber, companyNumberRef]);
  
  // 전화번호 입력 핸들러
  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (telRef.current) {
      telRef.current.value = formatted;
    }
  }, [formatPhoneNumber, telRef]);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // 검색 전에 포맷팅 적용
      let formattedCompanyNumber = companyNumberRef.current?.value || '';
      let formattedTelNo = telRef.current?.value || '';

      // URL 쿼리 파라미터 업데이트
      const queryParams = {
        size: pageSize,
        telNo: formattedTelNo || undefined,
        companyName: companyNameRef.current?.value || undefined,
        companyNumber: formattedCompanyNumber || undefined,
        ceo: ceoRef.current?.value || undefined,
        id: idRef.current?.value || undefined,
        page: '0', // 검색 시 첫 페이지로,
        bizProfileName: bizRef.current?.value || undefined,
      };

      // undefined 값 제거
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key as keyof typeof queryParams] === undefined) {
          delete queryParams[key as keyof typeof queryParams];
        }
      });

      // 검색 시에는 기존 쿼리를 전체 교체 (지워진 파라미터도 반영)
      updateQueryString(queryParams, true);

      // API 호출은 URL 변경 후 useEffect에서 처리됨
    },
    [pageSize, companyNumberRef, telRef, companyNameRef, ceoRef, idRef, formatBusinessNumber, formatPhoneNumber, updateQueryString]
  );

  const sizeOptions = [
    { option: '20개씩', value: '20' },
    { option: '50개씩', value: '50' },
    { option: '100개씩', value: '100' },
    { option: '300개씩', value: '300' },
    { option: '500개씩', value: '500' },
  ];

  return (
    <div className="!w-full">
      <div className="!flex !justify-start !items-center">
        <button
          onClick={() => setIsFilterOpen(prev => !prev)}
          className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
        >
          {isFilterOpen ? '닫기' : '검색필터 열기'}
        </button>
      </div>
      <form
        onSubmit={handleSearch}
        className={`
          ${isFilterOpen ? '!flex' : '!hidden'}
          md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center md:!justify-between !w-full
        `}
      >
        <div className="!flex !items-center !gap-3">
          <div className="!flex !items-center !gap-2 !flex-wrap">
            <Input
              inputRef={idRef}
              textAlign="left"
              width={200}
              placeholder="아이디"
            />
            <Input
              inputRef={companyNameRef}
              textAlign="left"
              width={200}
              placeholder="회사명"
            />
            <Input
              inputRef={ceoRef}
              textAlign="left"
              width={160}
              placeholder="대표"
            />
            <Input
                inputRef={companyNumberRef}
                textAlign="left"
                width={200}
                placeholder="사업자번호"
                onChange={handleBusinessNumberChange}
            />
            <Input
                inputRef={bizRef}
                textAlign="left"
                width={200}
                placeholder="비즈프로필"
            />
            <div className="!relative">
              <Input
                inputRef={telRef}
                textAlign="left"
                width={140}
                placeholder="대표전화"
                onChange={handlePhoneChange}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="submit"
                className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-8 !h-8 !bg-blue-400 hover:!bg-blue-500 !rounded !flex !items-center !justify-center !transition-colors !border-0"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-white !text-sm" />
              </button>
            </div>
          </div>
        </div>
        <div className="!flex !items-center self-start md:self-center">
          <Select
            options={sizeOptions}
            onChange={onChangePageSize}
            value={pageSize}
            style={{ width: '120px' }}
          />
        </div>
      </form>
    </div>
  );
};

export default FranchiseFilter;
